import { useCallback, useEffect, useRef, useState } from "react";
import {
  isInlineBannerAction,
  type AttentionBannerDescriptor,
  type AttentionBannerKind,
  type AttentionBannerLaunchTarget,
} from "./attention-banner";
import type { UseAttentionAnalyticsResult } from "./use-attention-analytics";
import { useResolvedBanner } from "./use-resolved-banner";

export interface UseAttentionBannerParams {
  menteeId: string | null;
  /** Banners render only on composing surfaces; admin read-only omits them. */
  enabled: boolean;
  /** Launch seam for tool chips; tracking is handled here. */
  onLaunchAction?: (
    kind: AttentionBannerKind,
    target: AttentionBannerLaunchTarget,
  ) => void;
  /** Single analytics instance threaded from the workspace so `teen_id` stays consistent. */
  analytics: UseAttentionAnalyticsResult;
}

export interface UseAttentionBannerResult {
  banner: AttentionBannerDescriptor | null;
  open: boolean;
  onToggle: () => void;
  onAction: (actionId: string) => void;
  mentorshipId: string | null;
}

/**
 * Data hook for the composer banner: React Query owns mentorship state,
 * `attention-banner.ts` owns pure trigger resolution, and this hook layers on
 * flag enablement, collapse UI state, and analytics.
 */
export function useAttentionBanner({
  menteeId,
  enabled,
  onLaunchAction,
  analytics,
}: UseAttentionBannerParams): UseAttentionBannerResult {
  const [open, setOpen] = useState(true);

  const { banner, mentorshipId } = useResolvedBanner({
    menteeId,
    enabled,
  });

  const viewedKind = useRef<AttentionBannerKind | null>(null);
  useEffect(() => {
    if (banner === null) {
      viewedKind.current = null;
      return;
    }
    if (viewedKind.current === banner.kind) return;
    viewedKind.current = banner.kind;
    analytics.trackBannerView(banner.kind);
  }, [banner, analytics]);

  const onToggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (banner) analytics.trackBannerToggle(banner.kind, next);
      return next;
    });
  }, [banner, analytics]);

  const onAction = useCallback(
    (actionId: string) => {
      if (!banner) return;
      analytics.trackBannerAction(banner.kind, actionId);
      const action = banner.actions.find((item) => item.id === actionId);
      if (!action || isInlineBannerAction(action)) return;
      onLaunchAction?.(banner.kind, action.target);
    },
    [banner, analytics, onLaunchAction],
  );

  return {
    banner,
    open,
    onToggle,
    onAction,
    mentorshipId,
  };
}
